import React, { useState } from "react";
import { contactAPI } from "../services/api";
import ScrollReveal, { RevealUp, RevealLeft, RevealRight, RevealScale, StaggeredReveal } from '../components/ScrollReveal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await contactAPI.create(formData);
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      alert("संदेश भेजने में त्रुटि हुई। कृपया पुनः प्रयास करें।");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-mithila-bg flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-3xl p-8 shadow-xl">
          <div className="text-6xl mb-4 text-green-600">
            <i className="fas fa-check-circle"></i>
          </div>
          <h2 className="text-2xl font-serif font-bold text-mithila-text mb-4">
            संदेश भेजा गया!
          </h2>
          <p className="text-gray-600 mb-6">
            आपका संदेश सफलतापूर्वक भेजा गया है। हम जल्द ही आपसे संपर्क करेंगे।
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="bg-mithila-primary text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-bold"
          >
            नया संदेश भेजें
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-temple-bg">
      {/* Header with Ram-Sita Background */}
      <section className="py-12 ram-sita-bg text-white">
        <RevealUp className="max-w-5xl mx-auto px-4 sm:px-5 text-center">
          <h1 className="text-3xl md:text-5xl font-serif font-bold mb-4">
            संपर्क करें
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
            हमसे जुड़ें और अपने प्रश्न पूछें
          </p>
        </RevealUp>
      </section>

      {/* Contact Information */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <StaggeredReveal>
              {/* Address */}
              <div className="bg-temple-bg rounded-3xl p-8 text-center border border-temple-secondary/20">
                <div className="text-4xl mb-4 text-temple-primary">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-temple-text mb-4">
                  पता
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p>ग्राम + पोo - फुलहर धाम</p>
                  <p>थाना - हरलाखी</p>
                  <p>जिला - मधुबनी</p>
                  <p>राज्य - बिहार</p>
                  <p>पिन कोड - 847240</p>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-temple-bg rounded-3xl p-8 text-center border border-temple-secondary/20">
                <div className="text-4xl mb-4 text-temple-primary">
                  <i className="fas fa-phone"></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-temple-text mb-4">
                  फोन
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p className="font-bold text-temple-primary">+91 9470820521</p>
                  <p className="text-sm">सुबह 8:00 - शाम 8:00</p>
                  <p className="text-sm">सभी दिन उपलब्ध</p>
                </div>
              </div>

              {/* Email */}
              <div className="bg-temple-bg rounded-3xl p-8 text-center border border-temple-secondary/20">
                <div className="text-4xl mb-4 text-temple-primary">
                  <i className="fas fa-envelope"></i>
                </div>
                <h3 className="text-xl font-serif font-bold text-temple-text mb-4">
                  ईमेल
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p className="font-bold text-blue-600 break-all">
                    shreesitaramjimandir@gmail.com
                  </p>
                  <p className="text-sm">24 घंटे में जवाब</p>
                  <p className="text-sm">सभी प्रश्नों का स्वागत</p>
                </div>
              </div>
            </StaggeredReveal>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-5">
          <RevealScale className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="bg-temple-primary text-white p-8 text-center">
              <h2 className="text-3xl font-serif font-bold mb-2">
                संदेश भेजें
              </h2>
              <p className="opacity-90">हमें अपने प्रश्न या सुझाव भेजें</p>
            </div>

            <ScrollReveal animation="up" delay={300}>
              <form onSubmit={handleSubmit} className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-temple-text mb-2">
                      नाम *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-temple-primary focus:ring-0 transition"
                      placeholder="आपका पूरा नाम"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-temple-text mb-2">
                      ईमेल *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-temple-primary focus:ring-0 transition"
                      placeholder="आपका ईमेल पता"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-temple-text mb-2">
                      फोन नंबर
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-temple-primary focus:ring-0 transition"
                      placeholder="आपका फोन नंबर"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-temple-text mb-2">
                      विषय *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-temple-primary focus:ring-0 transition"
                      placeholder="संदेश का विषय"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-temple-text mb-2">
                      संदेश *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="6"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-temple-primary focus:ring-0 transition resize-none"
                      placeholder="अपना संदेश यहाँ लिखें..."
                    />
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-temple-primary text-white px-8 py-4 rounded-lg hover:bg-red-700 transition font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="loader mr-2"></div>
                        भेजा जा रहा है...
                      </span>
                    ) : (
                      "संदेश भेजें"
                    )}
                  </button>
                </div>
              </form>
            </ScrollReveal>
          </RevealScale>
        </div>
      </section>

      {/* Visit Information */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <RevealUp className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-temple-text mb-3">
              दर्शन की जानकारी
            </h2>
            <div className="w-16 h-1 bg-temple-primary mx-auto"></div>
          </RevealUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StaggeredReveal>
              <div className="text-center">
                <div className="text-3xl mb-3 text-temple-secondary">
                  <i className="fas fa-sun"></i>
                </div>
                <h4 className="font-bold text-temple-text mb-2">प्रातः आरती</h4>
                <p className="text-gray-600">सुबह 6:00 बजे</p>
              </div>

              <div className="text-center">
                <div className="text-3xl mb-3 text-temple-secondary">
                  <i className="fas fa-clock"></i>
                </div>
                <h4 className="font-bold text-temple-text mb-2">दर्शन समय</h4>
                <p className="text-gray-600">सुबह 6:00 - शाम 8:00</p>
              </div>

              <div className="text-center">
                <div className="text-3xl mb-3 text-temple-secondary">
                  <i className="fas fa-moon"></i>
                </div>
                <h4 className="font-bold text-temple-text mb-2">संध्या आरती</h4>
                <p className="text-gray-600">शाम 7:00 बजे</p>
              </div>

              <div className="text-center">
                <div className="text-3xl mb-3 text-temple-secondary">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <h4 className="font-bold text-temple-text mb-2">विशेष उत्सव</h4>
                <p className="text-gray-600">रामनवमी, विवाह पंचमी</p>
              </div>
            </StaggeredReveal>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 bg-temple-bg">
        <div className="max-w-3xl mx-auto px-4 sm:px-5">
          <RevealUp className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-temple-text mb-3">
              अक्सर पूछे जाने वाले प्रश्न
            </h2>
            <div className="w-16 h-1 bg-temple-primary mx-auto"></div>
          </RevealUp>

          <div className="space-y-6">
            <StaggeredReveal>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-bold text-temple-text mb-2">
                  Q: मंदिर का दर्शन समय क्या है?
                </h4>
                <p className="text-gray-700">
                  A: मंदिर प्रतिदिन सुबह 6:00 बजे से शाम 8:00 बजे तक खुला रहता है।
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-bold text-temple-text mb-2">
                  Q: क्या यहाँ प्रसाद की व्यवस्था है?
                </h4>
                <p className="text-gray-700">
                  A: हाँ, मंदिर में प्रसाद की पूर्ण व्यवस्था है। विशेष अवसरों पर
                  निःशुल्क भंडारा भी होता है।
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-bold text-temple-text mb-2">
                  Q: यहाँ ठहरने की व्यवस्था है क्या?
                </h4>
                <p className="text-gray-700">
                  A: तीर्थ यात्रियों के लिए विश्राम गृह की व्यवस्था है। अधिक
                  जानकारी के लिए संपर्क करें।
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-bold text-temple-text mb-2">
                  Q: दान कैसे करें?
                </h4>
                <p className="text-gray-700">
                  A: आप ऑनलाइन दान कर सकते हैं या सीधे बैंक ट्रांसफर कर सकते हैं।
                  विस्तृत जानकारी के लिए दान पेज देखें।
                </p>
              </div>
            </StaggeredReveal>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <RevealUp className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-temple-text mb-3">
              स्थान
            </h2>
            <div className="w-16 h-1 bg-temple-primary mx-auto"></div>
          </RevealUp>

          <RevealScale className="bg-temple-bg rounded-3xl p-12 text-center border border-temple-secondary/20">
            <div className="text-6xl mb-4 text-temple-primary">
              <i className="fas fa-map"></i>
            </div>
            <h3 className="text-2xl font-bold text-temple-text mb-4">
              मानचित्र जल्द आ रहा है
            </h3>
            <p className="text-gray-600 mb-6">
              फुलहर धाम का विस्तृत मानचित्र और दिशा-निर्देश जल्द ही उपलब्ध
              होंगे।
            </p>
            <p className="text-sm text-gray-500">
              अभी के लिए कृपया फोन पर संपर्क करके दिशा-निर्देश प्राप्त करें।
            </p>
          </RevealScale>
        </div>
      </section>
    </div>
  );
};

export default Contact;
